import { hideDrawer } from '@/app/common/reducers/drawer';
import {
  useDeleteKeywordTrackerAnalysisHistoryMutation,
  useGetKeywordTrackerAnalysisHistoryDetailsQuery,
  useGetKeywordTrackerAnalysisHistoryQuery,
  useKeywordTrackerAnalysisMutation
} from '@/app/KeywordTracker/api/aiApi';
import { useGetAiUserSettingsQuery } from '@/app/Settings/api/aiSettingsApi';
import { timeRangeOptions } from '@/app/KeywordTracker/constants/aiModalConstants';
import { AI_WALLET_INSUFFICIENT_CODE } from '@/app/common/components/AiInsufficientBalanceModal';
import {
  AI_NO_ACTIVE_PROVIDER_KEY_CODE,
  resolveAiFormAlertMessage
} from '@/app/common/validators/aiMessages';
import type {
  AiModalPayload,
  ChatMessage,
  DrawerRootState,
  KeywordTypeValue,
  ModalSize,
  TimeRangeValue
} from '@/app/KeywordTracker/types/aiModalTypes';
import type { KeywordTrackerAnalysisHistoryItem } from '@/app/KeywordTracker/types/aiApiTypes';
import type { ProviderKey } from '@/app/Settings/types/aiSettingsApiTypes';
import useDomain from '@/hooks/useDomain';
import type { MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AI_SENDER_NAME, DEFAULT_MODAL_SIZE } from '../constants';

const DEFAULT_ANALYSIS_DAYS = 30;
const EMPTY_HISTORY_LIST: KeywordTrackerAnalysisHistoryItem[] = [];
const EMPTY_CHAT_HISTORY: ChatMessage[] = [];
const EMPTY_PROVIDER_KEYS: ProviderKey[] = [];

function normalizeApiCode(code: unknown): number | undefined {
  if (typeof code === 'number' && Number.isFinite(code)) return code;
  if (typeof code === 'string' && code.trim() !== '') {
    const parsed = Number(code);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function isInsufficientAiBalance(code?: number | null) {
  return code === AI_WALLET_INSUFFICIENT_CODE;
}

function isNoActiveApiKey(code?: number | null) {
  return code === AI_NO_ACTIVE_PROVIDER_KEY_CODE;
}

function getErrorCode(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) {
    return undefined;
  }
  const asRecord = err as { data?: unknown; code?: unknown };
  if (asRecord.data && typeof asRecord.data === 'object') {
    const fromData = normalizeApiCode(
      (asRecord.data as { code?: unknown }).code
    );
    if (fromData !== undefined) return fromData;
  }
  return normalizeApiCode(asRecord.code);
}

function getErrorMsg(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) {
    return null;
  }
  const asRecord = err as { data?: unknown; msg?: unknown };
  if (asRecord.data && typeof asRecord.data === 'object') {
    const fromData = (asRecord.data as { msg?: unknown }).msg;
    if (typeof fromData === 'string' && fromData.trim()) {
      return fromData.trim();
    }
  }
  if (typeof asRecord.msg === 'string' && asRecord.msg.trim()) {
    return asRecord.msg.trim();
  }
  return null;
}

function buildInitialAnalysisMessages(
  keyword: string | undefined,
  aiMessage: string
): ChatMessage[] {
  return [
    {
      id: Date.now() - 1,
      type: 'user',
      body: { message: `کلمه کلیدی "${keyword}" را تحلیل کن` },
      timestamp: new Date().toISOString(),
      sender: { name: 'شما' }
    },
    {
      id: Date.now(),
      type: 'ai',
      body: { message: aiMessage },
      timestamp: new Date().toISOString(),
      sender: { name: AI_SENDER_NAME }
    }
  ];
}

export const useAiModal = () => {
  const { key, payload } = useSelector(
    (state: DrawerRootState) => state.drawer
  );
  const dispatch = useDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const analysisRequestedRef = useRef<string | null>(null);
  const skipAutoAnalysisRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<
    'research' | 'analysis' | 'message' | null
  >(null);
  const { domain_slug } = useDomain();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showDeleteHistoryConfirm, setShowDeleteHistoryConfirm] =
    useState(false);
  const [showInsufficientBalance, setShowInsufficientBalance] =
    useState(false);
  const [showNoActiveApiKeyAlert, setShowNoActiveApiKeyAlert] =
    useState(false);
  const [formWarningMessage, setFormWarningMessage] = useState<string | null>(
    null
  );
  const [suppressAnalysisLoading, setSuppressAnalysisLoading] =
    useState(false);
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<TimeRangeValue | null>(null);
  const [selectedType, setSelectedType] =
    useState<KeywordTypeValue>('suggested');
  const [size, setSize] = useState<ModalSize>(DEFAULT_MODAL_SIZE);

  const { data: aiUserSettings, isFetching: isFetchingAiSettings } =
    useGetAiUserSettingsQuery(
      {},
      {
        skip: key !== 'ai_notes',
        refetchOnMountOrArgChange: true
      }
    );

  const aiSettingsFlags = aiUserSettings?.settings;
  const providerKeys = aiUserSettings?.provider_keys ?? EMPTY_PROVIDER_KEYS;
  const hasLoadedAiSettings = aiUserSettings !== undefined;

  // settings=null → اجازه استفاده (اعتبار AI / بینش)؛ بدون وابستگی به تنظیمات برای ارسال تحلیل
  const needsApiKeySetup = useMemo(
    () =>
      hasLoadedAiSettings &&
      Boolean(aiSettingsFlags?.enable_user_ai) &&
      providerKeys.length === 0,
    [hasLoadedAiSettings, aiSettingsFlags?.enable_user_ai, providerKeys]
  );

  const canUseAi = useMemo(() => {
    if (!hasLoadedAiSettings || aiSettingsFlags == null) return true;
    return (
      Boolean(aiSettingsFlags.enable_binesh_ai) ||
      (Boolean(aiSettingsFlags.enable_user_ai) && providerKeys.length > 0)
    );
  }, [hasLoadedAiSettings, aiSettingsFlags, providerKeys]);

  const keywordId = payload?.keyword_id ? String(payload.keyword_id) : '';

  const skipModalQueries = !keywordId || key !== 'ai_notes';

  const {
    data: historyList = EMPTY_HISTORY_LIST,
    isFetching: isFetchingHistoryList,
    isSuccess: isHistoryListLoaded
  } = useGetKeywordTrackerAnalysisHistoryQuery(undefined, {
    skip: skipModalQueries,
    refetchOnMountOrArgChange: true
  });

  const hasKeywordHistory = useMemo(
    () => historyList.some(item => item.keyword_id === keywordId),
    [historyList, keywordId]
  );

  const {
    data: chatHistory = EMPTY_CHAT_HISTORY,
    isFetching: isFetchingHistoryDetails
  } = useGetKeywordTrackerAnalysisHistoryDetailsQuery(keywordId, {
    skip:
      skipModalQueries || !isHistoryListLoaded || !hasKeywordHistory,
    refetchOnMountOrArgChange: true
  });

  const [
    keywordTrackerAnalysis,
    { isLoading: isKeywordTrackerAnalysisLoading }
  ] = useKeywordTrackerAnalysisMutation();

  const isFetchingAnalysis =
    isKeywordTrackerAnalysisLoading && pendingAction !== 'research';
  const isResearching =
    isKeywordTrackerAnalysisLoading && pendingAction === 'research';

  const [deleteHistory, { isLoading: isDeletingHistory }] =
    useDeleteKeywordTrackerAnalysisHistoryMutation();

  useEffect(() => {
    analysisRequestedRef.current = null;
    skipAutoAnalysisRef.current = false;
    setSuppressAnalysisLoading(false);
    setMessages(EMPTY_CHAT_HISTORY);
    setShowDeleteHistoryConfirm(false);
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
    setPendingAction(null);
  }, [keywordId]);

  useEffect(() => {
    if (!key) {
      setMessages(prev => (prev.length === 0 ? prev : EMPTY_CHAT_HISTORY));
      analysisRequestedRef.current = null;
      return;
    }

    if (isKeywordTrackerAnalysisLoading) return;

    if (hasKeywordHistory && !isFetchingHistoryDetails) {
      setMessages(prev => (prev === chatHistory ? prev : chatHistory));
    }
  }, [
    chatHistory,
    key,
    isKeywordTrackerAnalysisLoading,
    hasKeywordHistory,
    isFetchingHistoryDetails
  ]);

  const handleAnalysisCode = (
    rawCode?: unknown,
    rawMsg?: string | null
  ): boolean => {
    const code = normalizeApiCode(rawCode);
    if (code == null || code === 200) {
      return false;
    }
    if (isInsufficientAiBalance(code)) {
      setShowInsufficientBalance(true);
      setSuppressAnalysisLoading(true);
      return true;
    }
    if (isNoActiveApiKey(code)) {
      setShowNoActiveApiKeyAlert(true);
      setSuppressAnalysisLoading(true);
      return true;
    }
    setFormWarningMessage(
      resolveAiFormAlertMessage(
        code,
        rawMsg,
        'در ارتباط با سرویس تحلیل AI خطایی رخ داد.'
      )
    );
    setSuppressAnalysisLoading(true);
    return true;
  };

  const settleAnalysisWithoutMessage = () => {
    setSuppressAnalysisLoading(true);
  };

  useEffect(() => {
    if (key !== 'ai_notes') return;
    if (!domain_slug || !keywordId) return;
    if (!isHistoryListLoaded || hasKeywordHistory || skipAutoAnalysisRef.current)
      return;

    const requestKey = `${domain_slug}-${keywordId}`;
    if (analysisRequestedRef.current === requestKey) return;
    analysisRequestedRef.current = requestKey;
    setPendingAction('analysis');
    setSuppressAnalysisLoading(false);
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);

    keywordTrackerAnalysis({
      domain_slug,
      keyword_id: keywordId,
      number_of_days: DEFAULT_ANALYSIS_DAYS
    })
      .unwrap()
      .then(res => {
        if (handleAnalysisCode(res?.code, res?.msg)) return;
        if (res?.data?.message) {
          setMessages(
            buildInitialAnalysisMessages(payload?.keyword, res.data.message)
          );
          return;
        }
        settleAnalysisWithoutMessage();
      })
      .catch(err => {
        const handled = handleAnalysisCode(getErrorCode(err), getErrorMsg(err));
        if (!handled) {
          analysisRequestedRef.current = null;
          settleAnalysisWithoutMessage();
        }
      })
      .finally(() => {
        setPendingAction(null);
      });
  }, [
    key,
    domain_slug,
    keywordId,
    isHistoryListLoaded,
    hasKeywordHistory,
    keywordTrackerAnalysis,
    payload?.keyword
  ]);

  const handleClose = () => {
    dispatch(hideDrawer());
    setMessages([]);
    setSelectedTimeRange(null);
    setShowDeleteHistoryConfirm(false);
    analysisRequestedRef.current = null;
    skipAutoAnalysisRef.current = false;
    setSuppressAnalysisLoading(false);
    setPendingAction(null);
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
  };

  const handleDeleteHistoryRequest = () => {
    setShowDeleteHistoryConfirm(true);
  };

  const handleDeleteHistoryConfirm = () => {
    if (!keywordId) return;

    skipAutoAnalysisRef.current = true;
    setSuppressAnalysisLoading(true);
    setMessages(EMPTY_CHAT_HISTORY);

    deleteHistory({ keyword_id: keywordId })
      .unwrap()
      .then(() => {
        analysisRequestedRef.current = null;
        setShowDeleteHistoryConfirm(false);
      })
      .catch(() => {
        skipAutoAnalysisRef.current = false;
        setSuppressAnalysisLoading(false);
      });
  };

  const getAnalysisDays = (timeRange: TimeRangeValue | null) => {
    if (!timeRange) return DEFAULT_ANALYSIS_DAYS;

    return (
      timeRangeOptions.find(option => option.value === timeRange)?.days ??
      DEFAULT_ANALYSIS_DAYS
    );
  };

  const handleReanalyze = () => {
    if (!domain_slug || !keywordId) return;

    const numberOfDays = getAnalysisDays(selectedTimeRange);
    setMessages([]);
    setPendingAction('analysis');
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
    setSuppressAnalysisLoading(false);

    keywordTrackerAnalysis({
      domain_slug,
      keyword_id: keywordId,
      number_of_days: numberOfDays
    })
      .unwrap()
      .then(res => {
        if (handleAnalysisCode(res?.code, res?.msg)) return;
        if (res?.data?.message) {
          setMessages(
            buildInitialAnalysisMessages(payload?.keyword, res.data.message)
          );
          return;
        }
        settleAnalysisWithoutMessage();
      })
      .catch(err => {
        if (!handleAnalysisCode(getErrorCode(err), getErrorMsg(err))) {
          settleAnalysisWithoutMessage();
        }
      })
      .finally(() => {
        setPendingAction(null);
      });
  };

  const handleResearch = () => {
    if (!domain_slug || !keywordId) return;

    setPendingAction('research');
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);

    keywordTrackerAnalysis({
      domain_slug,
      keyword_id: keywordId,
      type: [selectedType],
      number_of_days: DEFAULT_ANALYSIS_DAYS
    })
      .unwrap()
      .then(res => {
        if (handleAnalysisCode(res?.code, res?.msg)) return;
        if (res?.data?.message) {
          setMessages(prev => [
            ...prev,
            {
              id: Date.now() + 1,
              type: 'ai',
              body: { message: res.data.message },
              timestamp: new Date().toISOString(),
              sender: { name: AI_SENDER_NAME }
            }
          ]);
        }
      })
      .catch(err => {
        handleAnalysisCode(getErrorCode(err), getErrorMsg(err));
      })
      .finally(() => {
        setPendingAction(null);
      });
  };

  const handleSendMessage = (messageText: string) => {
    if (!domain_slug || !keywordId) return Promise.resolve();

    const newMsg: ChatMessage = {
      id: Date.now(),
      type: 'user',
      body: { message: messageText },
      timestamp: new Date().toISOString(),
      sender: { name: 'شما' }
    };

    setMessages(prev => [...prev, newMsg]);
    setPendingAction('message');
    setShowInsufficientBalance(false);
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);

    return keywordTrackerAnalysis({
      domain_slug,
      keyword_id: keywordId,
      message: messageText
    })
      .unwrap()
      .then(res => {
        if (handleAnalysisCode(res?.code, res?.msg)) return;
        if (res?.data?.message) {
          setMessages(prev => [
            ...prev,
            {
              id: Date.now() + 1,
              type: 'ai',
              body: { message: res.data.message },
              timestamp: new Date().toISOString(),
              sender: { name: AI_SENDER_NAME }
            }
          ]);
        }
      })
      .catch(err => {
        handleAnalysisCode(getErrorCode(err), getErrorMsg(err));
      })
      .finally(() => {
        setPendingAction(null);
      });
  };

  const startResize = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!modalRef.current) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = modalRef.current.offsetWidth;
    const startHeight = modalRef.current.offsetHeight;

    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      setSize({
        width: Math.max(500, startWidth + (moveEvent.clientX - startX)),
        height: Math.max(300, startHeight + (moveEvent.clientY - startY))
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return {
    key,
    payload: payload as AiModalPayload | null,
    modalRef,
    messages,
    size,
    canUseAi,
    needsApiKeySetup,
    isFetchingAiSettings,
    isFetchingHistoryList,
    isFetchingHistoryDetails,
    hasKeywordHistory,
    isHistoryListLoaded,
    isFetchingAnalysis,
    suppressAnalysisLoading,
    isDeletingHistory,
    isSendingMessage:
      isKeywordTrackerAnalysisLoading && pendingAction === 'message',
    isResearching,
    selectedTimeRange,
    setSelectedTimeRange,
    selectedType,
    setSelectedType,
    handleClose,
    handleReanalyze,
    handleResearch,
    handleSendMessage,
    handleDeleteHistoryRequest,
    handleDeleteHistoryConfirm,
    showDeleteHistoryConfirm,
    setShowDeleteHistoryConfirm,
    showInsufficientBalance,
    showNoActiveApiKeyAlert,
    setShowNoActiveApiKeyAlert,
    formWarningMessage,
    setFormWarningMessage,
    startResize
  };
};
